import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'

import { customPageModuleQuery } from '@data/sanity/queries/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityPage, SanityPageType } from '@data/sanity/queries/types/page'
import { parseOptionalParameters } from '@lib/helpers'
import { getAllLocales, getLocaleLanguage, Locale } from '@lib/language'
import {
  BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext, StaticPathWithSlugs } from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getPage,
} from '@lib/sanity/page'

import Layout from '@components/layout'
import Modules from '@modules/modules'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type PageProps = BasePageProps & {
  slugs: string[]
  page: SanityPage
  site: SanitySiteFragment
}

const Page = ({ draftMode, locale, slugs, page, site }: PageProps) => {
  return (
    <Layout page={page} site={site} draftMode={draftMode}>
      {!draftMode && <Modules />}
      {draftMode && (
        <PreviewModules
          query={customPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            pageSlugs: slugs,
          }}
        />
      )}
    </Layout>
  )
}

interface PageParams extends ParsedUrlQuery {
  slugs: string[]
}

export async function getStaticProps(
  context: CustomStaticPropsContext<PageParams>,
): Promise<GetStaticPropsResult<PageProps>> {
  const locale = getLocaleLanguage(context.locale)

  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slugs = parseOptionalParameters(context.params?.slugs) ?? []
  const page = await getPage(sanityClient, locale, slugs.join('/'))

  if (!page.page || page.page.type === SanityPageType.TEMPLATE) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      slugs,
      page: page.page,
      site: page.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<PageParams>
> {
  const sanityClient = getSanityClient()
  const allPages = await getAllSlugs(sanityClient, 'page')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlugs[] = allPages
    .map((page) => ({ ...page, locale: page.locale as Locale }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slugs: slug.split('/').filter((slug) => !!slug),
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default Page
